// ProfileHeader.jsx
import React, { useState, useCallback } from 'react';
import './ProfileHeader.css';
import { Avatar, Typography, Modal, Box, Button, IconButton, Slider } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CloseIcon from '@mui/icons-material/Close';
import Cropper from 'react-easy-crop';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { db } from '../../firebase';

function ProfileHeader({userInfo, isOtherUser = false}) {
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [currentEditMode, setCurrentEditMode] = useState(null); // 'profile' or 'banner'

  const handleFileChange = async (event, type) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
        setCurrentEditMode(type);
        if (type === 'profile') {
          setIsProfileModalOpen(true);
        } else {
          setIsBannerModalOpen(true);
        }
      });
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.src = url;
    });

  const getCroppedImg = async () => {
    try {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg');
      });
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const handleSave = async () => {
    try {
      const croppedImage = await getCroppedImg();
      if (croppedImage && userInfo?.uid) {
        const storage = getStorage();
        const timestamp = new Date().getTime();
        const imageType = currentEditMode === 'profile' ? 'profilePictures' : 'bannerPictures';
        const storageRef = ref(storage, `${imageType}/${userInfo.uid}_${timestamp}.jpg`);
        
        await uploadBytes(storageRef, croppedImage);
        const imageUrl = await getDownloadURL(storageRef);
        
        const userRef = doc(db, "UserInformation", userInfo.uid);
        await updateDoc(userRef, {
          [currentEditMode === 'profile' ? 'profilePictureUrl' : 'bannerPictureUrl']: imageUrl
        });

        // Update local state
        if (currentEditMode === 'profile') {
          userInfo.profilePictureUrl = imageUrl;
          setIsProfileModalOpen(false);
        } else {
          userInfo.bannerPictureUrl = imageUrl;
          setIsBannerModalOpen(false);
        }
      }
    } catch (error) {
      console.error("Error updating image:", error);
      alert("Failed to update image. Please try again.");
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: '500px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
    p: 0,
    overflowY: 'auto',
    maxHeight: '90vh',
  }

  const renderCropperModal = (isOpen, onClose, type) => (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
    >
      <Box sx={modalStyle}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
            pb: 2
          }}>
            <Typography variant="h6">
              {type === 'profile' ? 'Crop Profile Picture' : 'Crop Banner Picture'}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{
            position: 'relative',
            width: '100%',
            height: type === 'profile' ? '50vh' : '45vh',
            backgroundColor: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            borderRadius: '12px',
            marginBottom: '24px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={type === 'profile' ? 1 : 1200/236}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                objectFit="contain"
                cropShape={type === 'profile' ? "round" : "rect"}
                showGrid={false}
              />
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 3,
              p: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              borderRadius: '8px'
            }}>
              <ZoomOutIcon sx={{ color: 'text.secondary' }} />
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="zoom-slider"
                onChange={(e, zoom) => setZoom(zoom)}
                sx={{
                  '& .MuiSlider-thumb': {
                    backgroundColor: '#007AFF',
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#007AFF',
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: '#E0E0E0',
                  }
                }}
              />
              <ZoomInIcon sx={{ color: 'text.secondary' }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  bgcolor: '#007AFF',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#0056b3'
                  },
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px'
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  return (
    <div>
      <div className="banner-container">
        <img 
          src={userInfo?.bannerPictureUrl || "https://images.pexels.com/photos/36717/amazing-animal-beautiful-beautifull.jpg"}
          alt="Banner"
          className="banner-image"
        />
        {!isOtherUser && (
          <>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="banner-picture-input"
              type="file"
              onChange={(e) => handleFileChange(e, 'banner')}
            />
            <label htmlFor="banner-picture-input">
              <IconButton
                component="span"
                className="banner-edit-icon"
                sx={{
                  position: 'absolute',
                  bottom: '20px',
                  right: '20px',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <EditIcon sx={{ fontSize: 20, color: '#666' }} />
              </IconButton>
            </label>
          </>
        )}
      </div>
      <div className="profile-section">
        <div className="avatar-container">
          <Avatar
            src={userInfo?.profilePictureUrl || ""}
            className="profile-avatar"
            alt={userInfo?.email || "User"}
            sx={{
              width: 160,
              height: 160,
              border: '4px solid white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              bgcolor: '#E9505F' // Fallback color when no image is available
            }}
          >
            {/* Display first letter of email if no image */}
            {userInfo?.email ? userInfo.email[0].toUpperCase() : '?'}
          </Avatar>
          {!isOtherUser && (
            <>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-picture-input"
                type="file"
                onChange={(e) => handleFileChange(e, 'profile')}
              />
              <label htmlFor="profile-picture-input">
                <IconButton
                  component="span"
                  className="edit-icon"
                  sx={{
                    position: 'absolute',
                    bottom: '10px',
                    right: '10px',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  <EditIcon sx={{ fontSize: 20, color: '#666' }} />
                </IconButton>
              </label>
            </>
          )}
        </div>
        <div className="profile-info">
          <Typography variant="h5" className="user-name">
          {userInfo ? userInfo.name : 'Loading...'}
          </Typography>
          <Typography variant="body1" className="user-email">
          {userInfo ? userInfo.email : 'Loading...'}
          </Typography>
        </div>
        {/* todo add later, not needed for now */}
        {/* <div class="stats-container">
          <div class="stat-box">
          <div className="number">
            {userInfo?.friends?.toLocaleString() || 0}
          </div>
            <div class="label">Friends</div>
          </div>
          <div class="stat-box">
          <div className="number">
            {userInfo?.following?.toLocaleString() || 0}
          </div>
            <div class="label">Following</div>
          </div>
        </div> */}
      </div>
      <div className="line"></div>

      {/* Profile Picture Cropper Modal */}
      {!isOtherUser && renderCropperModal(isProfileModalOpen, () => setIsProfileModalOpen(false), 'profile')}

      {/* Banner Picture Cropper Modal */}
      {!isOtherUser && renderCropperModal(isBannerModalOpen, () => setIsBannerModalOpen(false), 'banner')}
    </div>
  );
};

export default ProfileHeader;
